<script lang="ts">
    import { Button, Spinner, Input, Avatar, Tooltip, Checkbox } from '@allibee/components/basic';
    import { SortableTree, type SortableTreeNodeData } from '@allibee/components/common';
    import { OverlayScrollbars } from 'overlayscrollbars';
    import { createEventDispatcher, onMount } from 'svelte';
    import { MtmService } from '$lib/api';
    import { twMerge } from 'tailwind-merge';
    import { isEmpty, uniqBy } from 'lodash-es';
    import { getDivisionFullPath, highlightMatch, toastDanger, type DivisionNode } from '$lib/utils';
    import { _ } from 'svelte-i18n';
    import { page } from '$app/stores';
    import { UserOption } from '$lib/service';

    type GroupTreeNode = SortableTreeNodeData & {
        data: MtmGroupDto;
        nodes?: GroupTreeNode[];
    };
    type SortableTreeInstance = {
        findNode: (key: string, value: unknown) => { reveal: () => void } | null;
    };

    const dispatch = createEventDispatcher<{ confirm: MtmUserDto[]; cancel: void }>();
    let treeRef: HTMLDivElement, searchRef: HTMLDivElement, selectedRef: HTMLDivElement;
    let tree: SortableTreeInstance | null = null;

    export let onConfirm = (v?: any) => v;
    export let confirmText = $_('lib.service.modalContent.modalUserSelect.confirm');
    export let onCancel: (v?: any) => void = () => false;
    export let multiple = false;
    export let defaultItems: MtmUserDto[] | undefined;
    export let defaultDivisionName: string | undefined = undefined;

    // 전체 사용자 목록이 대상이 아닌 경우, 필터링 조건을 통해 모달에서 선택할 수 있는 사용자 목록을 제한
    export let allowedUsers: Array<{ value: number; label: string; data: MtmUserDto }> | undefined;
    $: allowedUserIds = allowedUsers?.map(user => user.value);
    $: allowedDivisionIds = allowedUsers?.map(user => user.data?.division?.id);

    const colorClass = 'text-primary-500';
    const bgColorClass = 'bg-primary-50';
    const hoverBgColorClass = 'hover:bg-primary-100';
    const borderColorClass = 'border-primary-500';

    let title = $_('lib.service.modalContent.modalUserSelect.selectUser');
    let loading = true;
    let searchLoading = false;
    let divisions: DivisionNode[] = [];
    let searchKeyword = '';
    let searchedKeyword = '';
    let searchedUsers: MtmUserDto[] = [];
    let selectedUsers: MtmUserDto[] = [];
    let confirmedUsers: MtmUserDto[] = defaultItems || [];
    let selectedDivisionId = $page.data.auth.workspaceUserInfo.groupId;
    let selectedDivisionName = '';

    const toUserDto = (user: MtmUserDto | MtmUserWithGroupsDto): MtmUserDto => ({
        ...user,
        external: 'external' in user ? user.external : false,
        products: 'products' in user ? user.products : [],
    });

    onMount(async () => {
        divisions = await MtmService.getDivisionTree().then(res => res.data.data as DivisionNode[]);
        loading = false;

        initializeScrollbars();

        if (defaultDivisionName) {
            await handleClickedByName(defaultDivisionName);
        }
        if (Array.isArray(defaultItems) && defaultItems.length) {
            if (multiple) {
                searchedUsers = defaultItems;
                selectedUsers = defaultItems;
            } else {
                searchedUsers = defaultItems.slice(0, 1);
                handleUserSelection(searchedUsers[0]);
            }
        }
    });

    const initializeScrollbars = () => {
        [treeRef, searchRef, selectedRef].forEach(ref => {
            if (ref) {
                OverlayScrollbars(ref, { scrollbars: { autoHide: 'never' } });
            }
        });
    };

    const handleConfirm = () => {
        const result = multiple ? confirmedUsers : selectedUsers?.length ? selectedUsers : [];
        dispatch('confirm', result);
        onConfirm?.(result);
    };

    const handleCancel = () => {
        dispatch('cancel');
        onCancel?.();
    };

    const convertDivisionToData = tree => {
        return (
            tree?.map(node => ({
                data: node.division,
                nodes: node.nodes ? convertDivisionToData(node.nodes) : [],
            })) || []
        );
    };

    let renderLabel = ((data: MtmGroupDto) => {
        // allowedUsers가 있으면 해당 목록에 포함되지 않은 division은 disable
        // allowedUsers가 undefined이면 모든 division이 enable
        const isDisabled = allowedDivisionIds !== undefined && !allowedDivisionIds?.includes(data.id);
        const disabledClass = isDisabled ? 'cursor-not-allowed text-gray-400' : '';

        return `
            <div id="render-label-${data.id}" class="flex gap-1 items-center text-left ${disabledClass}">
                ${data.name}
                <svg id="render-label-icon-${data.id}" class="hidden flex-none" width="10" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M13.3192 0.480806C13.5629 0.724592 13.6998 1.05519 13.6998 1.39991C13.6998 1.74462 13.5629 2.07522 13.3192 2.31901L6.11912 9.51905C5.87533 9.76276 5.54469 9.89971 5.19998 9.89971C4.85526 9.89971 4.52466 9.7628 4.28088 9.51909L0.675917 5.91413C0.439112 5.66895 0.308122 5.34053 0.311084 4.99967C0.314046 4.65881 0.450766 4.33276 0.691796 4.09173C0.932827 3.8507 1.25888 3.71398 1.59974 3.71101C1.94059 3.70805 2.26898 3.83909 2.51416 4.07589L2.51916 4.08072L5.19998 6.76162L11.4808 0.480763C11.7246 0.237051 12.0553 0.100098 12.4 0.100098C12.7447 0.100098 13.0754 0.237093 13.3192 0.480806Z" fill="currentColor"/>
                </svg>
            </div>
        `;
    }) as unknown as import('@allibee/components/common').SortableTreeRenderLabelFunction;

    let groupNodes: GroupTreeNode[] = [];
    $: if (!isEmpty(divisions)) groupNodes = convertDivisionToData(divisions);

    const handleSearch = async (keyword: string) => {
        if (keyword.length < 2) {
            toastDanger($_('lib.service.modalContent.modalUserSelect.searchMinLength'));
            return;
        }

        searchLoading = true;
        searchedKeyword = keyword;
        selectedUsers = [];
        selectedDivisionId = 0;
        selectedDivisionName = '';
        updateRenderLabel();

        const response = await MtmService.pageUsers({ keyword, size: 500 });
        searchedUsers = response.data.data.content || [];
        if (allowedUserIds) {
            searchedUsers = searchedUsers.filter(user => allowedUserIds.includes(user.id));
        }
        searchLoading = false;
    };

    const updateRenderLabel = () => {
        document.querySelectorAll<HTMLElement>(`[id^="render-label-"]`).forEach(el => el.classList.remove(colorClass));
        document.querySelectorAll<HTMLElement>(`[id^="render-label-icon-"]`).forEach(el => {
            el.classList.remove(colorClass, 'block');
            el.classList.add('hidden');
        });

        const selectedLabel = document.getElementById(`render-label-${selectedDivisionId}`);
        if (selectedLabel) selectedLabel.classList.add(colorClass);

        const selectedIcon = document.getElementById(`render-label-icon-${selectedDivisionId}`);
        if (selectedIcon) {
            selectedIcon.classList.remove('hidden');
            selectedIcon.classList.add(colorClass, 'block');
        }
    };

    const handleClicked = async (event, node) => {
        // allowedDivisionIds가 undefined이면 모든 division이 enable
        // allowedDivisionIds가 있으면 해당 목록에 포함되지 않은 division은 disable
        if (allowedDivisionIds !== undefined && !allowedDivisionIds.includes(node.data.id)) {
            return; // 클릭 이벤트 무시
        }

        selectedUsers = [];
        searchedKeyword = '';
        searchKeyword = '';

        const response = await MtmService.getDivisionUsersV2({ divisionId: node.data.id });

        searchedUsers = response.data.data.map(toUserDto);
        selectedDivisionId = node.data.id;
        selectedDivisionName = node.data.name;

        if (allowedUserIds) {
            searchedUsers = searchedUsers.filter(user => allowedUserIds.includes(user.id));
        }

        updateRenderLabel();
    };

    const handleClickedByName = async (divisionName: string) => {
        const findDivisionNode = (nodes: GroupTreeNode[]): GroupTreeNode | null => {
            for (const node of nodes) {
                if (node.data.name === divisionName) {
                    return node;
                }
                if (Array.isArray(node.nodes)) {
                    const found = findDivisionNode(node.nodes);
                    if (found) return found;
                }
            }
            return null;
        };

        const foundNode = findDivisionNode(groupNodes);
        if (foundNode) {
            await handleClicked(null, foundNode);
            selectedDivisionId = foundNode.data.id as number;
            selectedDivisionName = foundNode.data.name as string;
            updateRenderLabel();

            if (tree) {
                const node = tree.findNode('id', foundNode.data.id);
                if (node) {
                    node.reveal();
                }
            }
        }
    };

    const handleUserSelection = user => {
        const isSelected = selectedUsers.some(u => u.id === user.id);
        if (isSelected) {
            selectedUsers = selectedUsers.filter(u => u.id !== user.id);
            return;
        }
        const userWithDivision = selectedDivisionName ? { ...user, divisionName: selectedDivisionName } : user;
        if (multiple) selectedUsers = [...selectedUsers, userWithDivision];
        else {
            selectedUsers = [userWithDivision];
        }
    };

    const handleAddSelectedUsers = () => {
        confirmedUsers = uniqBy([...confirmedUsers, ...selectedUsers], 'id');
        selectedUsers = [];
    };

    const handleClearSelectedUsers = () => {
        confirmedUsers = [];
    };

    const icons = {
        collapsed: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="24" height="24" rx="4" fill="#E1E3EB"/><path d="M12 7.5V12M12 12V16.5M12 12H16.5M12 12H7.5" stroke="#636570" stroke-width="1.2" stroke-linecap="square" stroke-linejoin="round"/></svg>`,
        open: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="0.5" y="0.5" width="23" height="23" rx="3.5" fill="white"/><rect x="0.5" y="0.5" width="23" height="23" rx="3.5" stroke="#C7C9D1"/><path d="M16.5 12H7.5" stroke="#636570" stroke-width="1.2" stroke-linecap="square" stroke-linejoin="round"/></svg>`,
    };
</script>

<div class="flex h-full w-full flex-col text-center">
    <section class="flex items-center justify-between rounded-t-lg border-b p-6">
        <h2 class="whitespace-pre p-0 text-lg font-semibold text-gray-900 dark:text-white">
            {title || ''}
        </h2>

        <button on:click={handleCancel} type="button">
            <svg class="m-0" width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 5.5L19 19.5M5 19.5L19 5.5L5 19.5Z" stroke="#C7C9D1" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
        </button>
    </section>

    <div class="flex max-h-full flex-col gap-2 overflow-y-auto overscroll-contain bg-gray-50 px-6 py-10" role="document" on:wheel|stopPropagation|passive>
        <div class="flex h-[30rem] items-center gap-4">
            <div class="inline-flex h-full w-full overflow-hidden rounded-lg border border-gray-200 bg-white px-1">
                <div bind:this={treeRef} class="w-full">
                    <div class={twMerge('modal-user-tree inline-flex h-full w-full flex-col rounded-lg', loading && 'flex items-center justify-center')}>
                        {#if loading}
                            <Spinner size="8" />
                        {:else}
                            <SortableTree selectedNodeId={selectedDivisionId} bind:tree disableSorting initCollapseLevel={1} nodes={groupNodes} {renderLabel} {icons} onClick={handleClicked} />
                        {/if}
                    </div>
                </div>
                <div class="w-px bg-gray-200"></div>

                <div class="inline-flex w-full flex-col rounded-lg">
                    <div class="flex h-36 flex-col gap-4 bg-white p-6">
                        <div class="inline-flex gap-4">
                            <Input
                                placeholder={$_('lib.service.modalContent.modalUserSelect.inputUserName')}
                                bind:value={searchKeyword}
                                on:keydown={e => e.key === 'Enter' && handleSearch(searchKeyword)}
                            >
                                <div slot="left">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="19" viewBox="0 0 18 19" fill="none">
                                        <path
                                            d="M15.75 16.25L11.25 11.75M12.75 8C12.75 8.68944 12.6142 9.37213 12.3504 10.0091C12.0865 10.646 11.6998 11.2248 11.2123 11.7123C10.7248 12.1998 10.146 12.5865 9.50909 12.8504C8.87213 13.1142 8.18944 13.25 7.5 13.25C6.81056 13.25 6.12787 13.1142 5.49091 12.8504C4.85395 12.5865 4.2752 12.1998 3.78769 11.7123C3.30018 11.2248 2.91347 10.646 2.64963 10.0091C2.3858 9.37213 2.25 8.68944 2.25 8C2.25 6.60761 2.80312 5.27226 3.78769 4.28769C4.77226 3.30312 6.10761 2.75 7.5 2.75C8.89239 2.75 10.2277 3.30312 11.2123 4.28769C12.1969 5.27226 12.75 6.60761 12.75 8Z"
                                            stroke="#818388"
                                            stroke-width="1.6"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>
                                </div>
                            </Input>
                            <Button color="gray" on:click={() => handleSearch(searchKeyword)}>{$_('lib.service.modalContent.modalUserSelect.search')}</Button>
                        </div>
                        <div class="inline-flex items-center justify-between">
                            <div class="flex items-center gap-1">
                                <div class="flex items-center justify-center gap-1">
                                    <div class="text-sm text-gray-900">{$_('lib.service.modalContent.modalUserSelect.total')}</div>
                                    <div class="flex">
                                        <div class="text-sm font-bold text-gray-900">{searchedUsers?.length || 0}</div>
                                        <div class="text-sm text-gray-900">{$_('lib.service.modalContent.modalUserSelect.count')}</div>
                                    </div>
                                </div>
                            </div>
                            {#if multiple}
                                <Button
                                    color="gray"
                                    outline
                                    size="xs"
                                    on:click={() => {
                                        selectedUsers = selectedUsers.length > 0 ? [] : searchedUsers;
                                    }}
                                >
                                    <svg width="13" height="12" viewBox="0 0 13 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M3 6.5L5 8.5L10 3.5" stroke="#636570" stroke-linecap="square" stroke-linejoin="round" />
                                    </svg>
                                    {$_('lib.service.modalContent.modalUserSelect.selectAll')}
                                </Button>
                            {/if}
                        </div>
                    </div>
                    <div bind:this={searchRef} class="h-full w-full">
                        <div class="flex flex-col gap-4 px-6 pb-6">
                            {#if searchedUsers && searchedUsers.length > 0}
                                <div class="inline-flex gap-4">
                                    <div class="flex w-full flex-col items-center gap-2">
                                        {#if searchLoading}
                                            <Spinner size="8" />
                                        {:else}
                                            {#each searchedUsers as user}
                                                <UserOption {user} checked={selectedUsers.some(u => u.id === user.id)} on:click={() => handleUserSelection(user)} />
                                            {/each}
                                        {/if}
                                    </div>
                                </div>
                            {:else}
                                <div class="text-left text-sm text-gray-400">{$_('lib.service.modalContent.modalUserSelect.noSelectableUsers')}</div>
                            {/if}
                        </div>
                    </div>
                </div>
            </div>
            {#if multiple}
                <div class="flex flex-col gap-[0.625rem]">
                    <Button color="dark" on:click={handleAddSelectedUsers}>
                        {$_('lib.service.modalContent.modalUserSelect.add')}
                        <svg width="19" height="18" viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10.25 3.75L15.5 9L10.25 14.25M4.25 3.75L9.5 9L4.25 14.25" stroke="white" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </Button>
                    <Button color="dark" outline on:click={handleClearSelectedUsers}>
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8.25 14.25L3 9L8.25 3.75M14.25 14.25L9 9L14.25 3.75" stroke="#636570" stroke-width="1.2" stroke-linecap="square" stroke-linejoin="round" />
                        </svg>
                        {$_('lib.service.modalContent.modalUserSelect.resetSelection')}
                    </Button>
                </div>

                <div class="inline-flex h-full w-60 shrink-0 flex-col rounded-lg border border-gray-200 bg-white">
                    <div bind:this={selectedRef} class="w-full">
                        <div class="inline-flex w-full justify-between p-6">
                            <div class="shring-0 flex h-6 w-full justify-between">
                                <div class="text-base font-bold leading-normal text-black">{$_('lib.service.modalContent.modalUserSelect.selectedUser')}</div>
                                <div class="flex">
                                    <div class={twMerge('text-sm font-bold', colorClass)}>{confirmedUsers.length}</div>
                                    <div class=" text-sm text-gray-900">{$_('lib.service.modalContent.modalUserSelect.count')}</div>
                                </div>
                            </div>
                        </div>
                        <div class="shring-0 inline-flex w-full flex-col gap-4 px-6">
                            {#if confirmedUsers.length > 0}
                                {#each confirmedUsers as user}
                                    <div class="flex flex-row items-center gap-1 px-1 py-0.5">
                                        <div class="flex flex-row items-center gap-1 px-1 py-0.5">
                                            <Avatar size="xs" name={user.displayName} />
                                            <div class="justify-start text-center text-sm font-medium text-gray-900">{user.displayName}</div>
                                        </div>
                                        <Tooltip strategy="fixed">
                                            <div class="text-left">
                                                <div class="mb-0.5 font-bold">{user.displayName}</div>
                                                {#each user.groups || [] as group}
                                                    <div>{getDivisionFullPath(group.groupId, divisions, ' / ') || group.groupName}</div>
                                                {/each}
                                                <div>{user.jobTitle?.name || '-'} / {user.jobPosition?.name || '-'}</div>
                                                <div>{user.loginId || '-'}</div>
                                            </div>
                                        </Tooltip>
                                        <button
                                            on:click={() => {
                                                confirmedUsers = confirmedUsers.filter(u => u.id !== user.id);
                                            }}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="13" viewBox="0 0 12 13" fill="none">
                                                <path d="M3 3.5L9 9.5M3 9.5L9 3.5L3 9.5Z" stroke="#C7C9D1" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                                            </svg>
                                        </button>
                                    </div>
                                {/each}
                            {:else}
                                <div class="shring-0 text-left text-sm text-gray-400">{$_('lib.service.modalContent.modalUserSelect.noSelectedUser')}</div>
                            {/if}
                        </div>
                    </div>
                </div>
            {/if}
        </div>
    </div>
    <section class="flex items-center justify-end gap-2 rounded-b-lg border-t p-6">
        <Button color="dark" class="flex w-fit" on:click={handleConfirm}>
            <slot name="confirm">
                {@html confirmText}
            </slot>
        </Button>
    </section>
</div>

<style global lang="postcss">
    .tree__node {
        @apply py-1;
    }
    .modal-user-tree .tree {
        --st-label-height: 2.5rem;
        --st-subnodes-padding-left: 2.7rem;
        --st-collapse-icon-width: 1.5rem;
        --st-collapse-icon-height: 3.75rem;
        --st-collapse-icon-size: 0.9rem;

        @apply my-2 flex flex-col;
        sortable-tree-node > :nth-child(3) {
            top: -0.35rem;
            left: calc(var(--st-collapse-icon-width) * -1 + -0.3125rem);
        }

        &__label {
            @apply cursor-pointer;
        }

        &__subnodes {
            @apply overflow-hidden;
        }

        --line-color: #c7c9d1;
        &__node {
            @apply relative;

            &:before {
                @apply absolute;
                content: '';
                top: 0;
                height: 101%;
                left: calc((var(--st-subnodes-padding-left) / -2) + 0.25rem);
                border-left: 0.0625rem solid var(--line-color);
            }
            &:last-child:before {
                top: -4px;
                height: calc(100% - 1.1875rem);
            }
        }

        &:nth-child(1) > sortable-tree-node:nth-child(1):before {
            top: 24px;
            height: calc(100% - 24px);
        }

        &__label {
            @apply text-sm text-gray-900;
            position: relative;

            &:before {
                @apply absolute;
                content: '';
                left: -1.15rem;
                top: -1.5625rem;
                height: calc(100% + 0.375rem);
                width: 0.0625rem;
                border-left: 0.0625rem solid var(--line-color);
                mix-blend-mode: lighten;
            }
            &:first-child:before {
                content: none;
            }

            :last-child > &:before {
                height: calc(50% + 0.375rem);
            }

            &:after {
                content: '';
                position: absolute;
                left: -1.1rem;
                top: 0.0625rem;
                width: calc(1.4rem - 0.25rem);
                height: 50%;
                border-bottom: 0.0625rem solid var(--line-color);
                z-index: 1;
                mix-blend-mode: lighten;
            }
        }

        &__collapse {
            z-index: 3;
        }

        /* disabled 노드 스타일 */
        &__label.cursor-not-allowed {
            cursor: not-allowed !important;

            &:hover {
                background-color: transparent !important;
            }
        }
    }
</style>
